/**
 * The deployment registers' wire layer — Figma page 05 (v7 Block C).
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED
 * ===========================================================================
 * All four row shapes below are copied field-for-field from
 * `apps/api/src/modules/registers/registers.service.ts` (`DeploymentRegisterRow`,
 * `DeployedProjectRow`, `SignoffQueueRow`, `AcceptanceChaseRow`), and the envelope
 * from `countries/support/http`'s `paged`. Every timestamp is a `Date` on the server
 * and a STRING on the wire — JSON has no date type — so each is typed as the
 * string these screens actually receive, and each keeps the SERVER'S OWN
 * nullability. Widening a nullable field to non-null here is how a screen ends up
 * printing a blank cell where the API said "there is nothing".
 *
 * ===========================================================================
 * NO MONEY CROSSES THIS BOUNDARY EITHER
 * ===========================================================================
 * A deployment has no price, a deployed project carries no value column, an
 * outstanding signature is not an invoice, and a missing acceptance document has
 * no amount. None of the four rows carries `amount` or any other money field
 * (measured against `AcceptanceChaseRow` too — registers.service.ts:277-297, and
 * its own e2e pins "no money field rides on the row"), so the masking interceptor
 * is not engaged on these routes and nothing here needs the `isMaskedAmount`
 * discipline — the same standing `api.ts` records for Block B. If a "value"
 * column ever appears on one of these registers, this comment is where the
 * reader learns that the screens were written on the assumption it could not.
 *
 * ===========================================================================
 * ONE ERROR MAPPER PER FEATURE, AND THIS IS THE SAME FEATURE
 * ===========================================================================
 * `registerErrorText` is IMPORTED from `./api` and re-exported, not re-authored.
 * The house rule the quotes/work/CRF siblings keep is one mapper per FEATURE —
 * `api.ts`'s own header says a shared helper would grow a branch per feature until
 * nobody could tell which sentence any surface produces. Block C is not another
 * feature; it is three more screens in `features/registers/`. A second copy in
 * this folder would be two mappers for one register family and the exact drift
 * that rule exists to prevent. Its behaviour is already right for these routes:
 * 400 is passed through, and the sentences these routes answer with —
 * "kind must be one of: deploy, redeploy." and "queue must be one of: developer,
 * project." — are the only text that says what to type instead.
 */

import { apiRequest } from '../../lib/api';
// `work_type` is not in `@crf/shared` — it is an ENUM on the API's `Crf` entity — so the
// web declares it once in the CRF feature and a mirror test holds it to the server.
import type { WorkType } from '../crfs/domain';
import { registerErrorText } from './api';
import type { Paged } from './api';

export { registerErrorText };
export type { Paged };

/* ─────────────────────────────────────────────────────────────────── DTOs ── */

/** `deployments.kind` — the closed vocabulary migration 6 declares. */
export const DEPLOYMENT_KINDS = ['deploy', 'redeploy'] as const;
export type DeploymentKind = (typeof DEPLOYMENT_KINDS)[number];

/**
 * The two sign-off queues — `SIGNOFF_QUEUES`, registers.service.ts:60.
 *
 * NOT a column. `signoffs.status` is always 'signed' (nothing anywhere writes
 * 'pending'), so these name a state the service DERIVES, and they exist here only
 * so the filter has a vocabulary to show and the server has one to refuse a
 * stranger against.
 */
export const SIGNOFF_QUEUES = ['developer', 'project'] as const;
export type SignoffQueue = (typeof SIGNOFF_QUEUES)[number];

/** `DeploymentRegisterRow` — registers.service.ts:95-105. */
export interface DeploymentRegisterRow {
  id: string;
  crf_id: string;
  crf_doc_no: string;
  /**
   * WHICH KIND OF WORK — `crfs.work_type` (migration 37, design §5.2a).
   *
   * ⚠ THIS REGISTER ALWAYS CONTAINED PROJECTS. Its read has no predicate that could have
   * excluded one, so from the day migration 37 shipped it widened whether or not anybody
   * decided it should. Carrying the field is what turns that from a silent widening into
   * a stated one: a reader can now tell which rows are which, and a `?work_type=` filter
   * exists to narrow it deliberately.
   */
  work_type: WorkType;
  party_name: string;
  /** The work order the deployment was recorded on — what the row's action links to. */
  work_order_id: string;
  kind: DeploymentKind;
  /**
   * WHICH WAY the software went out / the date it was DUE (migration 62, R8-A).
   * Both NULL = unrecorded — the server's honest value for every row written before
   * the columns existed; the screen renders an em-dash, never a guess. `mode` is
   * typed `string | null`, not the enum: the server owns the vocabulary and a
   * stranger renders as itself (the kind pill's own rule). `due_date` is a bare
   * 'YYYY-MM-DD' (the server DATE_FORMATs it), so no timezone can shift the day.
   */
  mode: string | null;
  due_date: string | null;
  deployed_by: { id: string; name: string };
  /** Server-stamped at the moment of deployment, and what the register is ordered by. */
  deployed_at: string;
  note: string | null;
}

/** `DeployedProjectRow` — registers.service.ts:107-118. ONE ROW PER CRF. */
export interface DeployedProjectRow {
  crf_id: string;
  crf_doc_no: string;
  /**
   * WHICH KIND OF WORK — `crfs.work_type` (migration 37, design §5.2a).
   *
   * ⚠ THIS REGISTER ALWAYS CONTAINED PROJECTS. Its read has no predicate that could have
   * excluded one, so from the day migration 37 shipped it widened whether or not anybody
   * decided it should. Carrying the field is what turns that from a silent widening into
   * a stated one: a reader can now tell which rows are which, and a `?work_type=` filter
   * exists to narrow it deliberately.
   */
  work_type: WorkType;
  party_id: string;
  party_name: string;
  /** `crfs.status`, which this route narrows to 'deployed' | 'signed_off'. */
  status: string;
  /**
   * THE LATEST DEPLOYMENT'S FACTS — not a summary of all of them.
   *
   * Null when a CRF's status was set some other way and no deployment row exists.
   *
   * `last_deployed_by` arrives from a SEPARATE subselect that INNER JOINs `users`,
   * so its null condition is independently reachable in the SHAPE of the wire
   * contract — though not, today, in the data: `fk_deployments_deployed_by` is ON
   * DELETE RESTRICT (migration 6), so a user who has deployed cannot be deleted
   * and the name is null exactly when the timestamp is. The screen still renders
   * the two absences separately rather than deriving one from the other, because
   * the API types them separately and `attachments.created_by` is precedent for a
   * user FK being relaxed to SET NULL later. See the Block C report for the
   * mis-attribution that subselect would produce if it ever were.
   */
  last_deployed_at: string | null;
  last_deployed_by: string | null;
  last_kind: DeploymentKind | null;
  /**
   * HOW MANY DEPLOYMENTS THIS CRF HAS HAD. Deployments, not projects — the row
   * itself is the project. The screen labels it so nobody can read a `3` as three
   * projects, which is the one misreading this column invites.
   */
  deployment_count: number;
}

/** `SignoffQueueRow` — registers.service.ts:124-145. */
export interface SignoffQueueRow {
  crf_id: string;
  crf_doc_no: string;
  /**
   * WHICH KIND OF WORK — `crfs.work_type` (migration 37, design §5.2a).
   *
   * ⚠ THIS REGISTER ALWAYS CONTAINED PROJECTS. Its read has no predicate that could have
   * excluded one, so from the day migration 37 shipped it widened whether or not anybody
   * decided it should. Carrying the field is what turns that from a silent widening into
   * a stated one: a reader can now tell which rows are which, and a `?work_type=` filter
   * exists to narrow it deliberately.
   */
  work_type: WorkType;
  party_name: string;
  /** WHICH signature is outstanding. Derived by the service, never selected. */
  queue: SignoffQueue;
  /** The developer signature when it exists — the project queue's precondition. */
  developer_signed_at: string | null;
  developer_signed_by: string | null;
  /**
   * WHAT THE DEVELOPER SIGN-OFF ROW RECORDS ABOUT MAIL. That row and no other.
   *
   * It is null on the developer queue, where the row does not exist yet — the
   * absence is what put the CRF in that queue. The service shipped this wrong
   * once, as a bare "whichever signature we are waiting on" column that was
   * structurally NULL in both branches, and its header now records why a
   * permanently blank "Mail" heading is worse in a UI than no column at all.
   *
   * There is deliberately NO field for the PROJECT signature's mail, and the
   * screens draw no column for one: the row being waited on does not exist.
   *
   * Null is rendered "No record", never "Not sent". This system sends no sign-off
   * mail; `mail_status` is a fact an operator writes down about a message that
   * reached them. A negative DELIVERY state would describe a transport that does
   * not exist.
   */
  developer_mail_status: string | null;
  /** Whichever timestamp starts this queue's clock. Null if nothing starts it. */
  waiting_since: string | null;
  /**
   * Whole days since `waiting_since`, computed once by the server so every reader
   * agrees. NULL when the clock has no start — a CRF marked deployed with no
   * deployment row. Null is NOT zero: "0 days" claims the wait began today.
   */
  days_waiting: number | null;
}

/**
 * `AcceptanceChaseRow` — registers.service.ts:277-297 (Train F, fork 21).
 *
 * A PROJECT sign-off that is SIGNED and says nothing about the customer's written
 * acceptance: no `acceptance_attachment_id`, no `not_required = 1`. The record is
 * closed; the document that ends a dispute is not on file. DISJOINT from
 * `SignoffQueueRow` by construction — the queue lists OUTSTANDING signatures and
 * its WHERE excludes a signed project row structurally, which is why this is a
 * sibling route and not a `?queue=` value (registers.controller.ts:135-147).
 */
export interface AcceptanceChaseRow {
  crf_id: string;
  crf_doc_no: string;
  /** See the sibling rows — fork 1: the acceptance requirement is on EVERY job. */
  work_type: WorkType;
  party_name: string;
  /**
   * `crfs.status` as it stands — 'signed_off' for a row `sign()` closed, but
   * REPORTED rather than assumed: a raw legacy insert can leave the CRF
   * elsewhere, and this list is about the SIGN-OFF row's silence, not the CRF's
   * status. Carried on the wire; the screen does not currently draw it (its
   * header records that decision).
   */
  crf_status: string;
  /** The PROJECT signature this row is about — when, by whom. BOTH nullable:
   * `signed_at` only by raw legacy insert, `signed_by` when the user was since
   * deleted — and the screen renders the two absences separately. */
  signed_at: string | null;
  signed_by: string | null;
  /**
   * The legacy acknowledgement state the signer recorded — 'none' is the
   * column's default and the honest answer, not blank. NON-null on this row
   * (unlike `developer_mail_status` above: here the row EXISTS, that is the
   * premise of the list). Carried on the wire; not drawn — see the page header.
   */
  mail_status: string;
  /**
   * Whole days since the PROJECT signature — the age of the gap, computed once
   * by the server so every reader agrees. Null exactly when `signed_at` is.
   * Null is NOT zero: "0 days" would claim the signature happened today.
   */
  days_since_signed: number | null;
}

/* ─────────────────────────────────────────────────────────────────── reads ── */

export interface DeploymentRegisterFilters {
  /**
   * PASSED THROUGH UNVALIDATED, deliberately.
   *
   * The server owns this vocabulary and answers 400 naming it. Sanitising here
   * would mean a URL saying `?kind=bogus` quietly rendered the WHOLE register
   * while claiming to be filtered — the same wrong-answer-that-looks-right the
   * testing register's `?result=` records, and worse on this screen, because a
   * silently unfiltered deployment list reads as "everything went out once".
   */
  kind?: string;
  /** The route's own filter (`?crf=`). No screen sets it — see the page header. */
  crf?: string;
  page?: number;
  perPage?: number;
}

export async function fetchDeploymentRegister(
  countryCode: string,
  filters: DeploymentRegisterFilters = {},
  signal?: AbortSignal,
): Promise<Paged<DeploymentRegisterRow>> {
  return apiRequest<Paged<DeploymentRegisterRow>>(`/countries/${countryCode}/deployments`, {
    query: {
      kind: filters.kind,
      crf: filters.crf,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export interface DeployedProjectsFilters {
  /**
   * `?party=` is the route's ONLY filter and no screen sets it.
   *
   * It is typed here because the controller accepts it (`registers.controller.ts`
   * → `deployedProjects`), not because anything drives it: a filter that only a
   * hand-typed URL can set is a filter nobody can clear — the reason the DL file
   * register draws none either. The screen is therefore honestly unfiltered.
   */
  party?: string;
  page?: number;
  perPage?: number;
}

export async function fetchDeployedProjects(
  countryCode: string,
  filters: DeployedProjectsFilters = {},
  signal?: AbortSignal,
): Promise<Paged<DeployedProjectRow>> {
  return apiRequest<Paged<DeployedProjectRow>>(`/countries/${countryCode}/deployed-projects`, {
    query: {
      party: filters.party,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export interface SignoffQueueFilters {
  /** PASSED THROUGH UNVALIDATED — same reasoning as `kind` above. */
  queue?: string;
  page?: number;
  perPage?: number;
}

export async function fetchSignoffQueue(
  countryCode: string,
  filters: SignoffQueueFilters = {},
  signal?: AbortSignal,
): Promise<Paged<SignoffQueueRow>> {
  return apiRequest<Paged<SignoffQueueRow>>(`/countries/${countryCode}/signoffs`, {
    query: {
      queue: filters.queue,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export interface AcceptanceChaseFilters {
  /**
   * `?work_type=` is the route's ONLY filter and no screen sets it — the
   * `?party=` precedent above: typed because the controller accepts it
   * (`registers.controller.ts` → `acceptanceChase`), not because anything
   * drives it, and PASSED THROUGH UNVALIDATED for the same reason as `kind` —
   * the server owns the vocabulary and answers 400 naming it.
   */
  work_type?: string;
  page?: number;
  perPage?: number;
}

export async function fetchAcceptanceChase(
  countryCode: string,
  filters: AcceptanceChaseFilters = {},
  signal?: AbortSignal,
): Promise<Paged<AcceptanceChaseRow>> {
  return apiRequest<Paged<AcceptanceChaseRow>>(
    `/countries/${countryCode}/signoffs/acceptance-chase`,
    {
      query: {
        work_type: filters.work_type,
        page: filters.page,
        perPage: filters.perPage,
      },
      signal,
    },
  );
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

/**
 * Keyed under the same `['registers', cc, …]` root as `registerKeys` so one
 * invalidation reaches the whole register family, and declared here rather than
 * bolted onto that object because Block B owns `api.ts`.
 */
export const deploymentKeys = {
  deployments: (cc: string, filters: DeploymentRegisterFilters) =>
    ['registers', cc, 'deployments', filters] as const,
  deployedProjects: (cc: string, filters: DeployedProjectsFilters) =>
    ['registers', cc, 'deployed-projects', filters] as const,
  signoffs: (cc: string, filters: SignoffQueueFilters) =>
    ['registers', cc, 'signoffs', filters] as const,
  acceptanceChase: (cc: string, filters: AcceptanceChaseFilters) =>
    ['registers', cc, 'acceptance-chase', filters] as const,
};
